import { useTranslation } from 'react-i18next';
import type { HostWorkspace } from '@/api/hostWorkspace/hostWorkspaceApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { environment } from '@/config/environment';
import { clearActiveWorkspaceSelection, clearAuthenticationTokens } from '@/lib/authStorage';
import { buildPlatformUrl } from '@/lib/hostWorkspaceBinding';

interface DomainWorkspaceMismatchScreenProps {
  hostWorkspace: HostWorkspace;
}

const ROOT_PATH = '/';

export function DomainWorkspaceMismatchScreen({
  hostWorkspace,
}: DomainWorkspaceMismatchScreenProps) {
  const { t } = useTranslation('common');
  const workspaceName =
    hostWorkspace.branding !== null && hostWorkspace.branding.brandName !== null
      ? hostWorkspace.branding.brandName
      : hostWorkspace.hostname;
  const hasPlatformUrl = environment.platformUrl.length > 0;

  function signOutHere() {
    clearAuthenticationTokens();
    clearActiveWorkspaceSelection();
    window.location.assign(ROOT_PATH);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zoooffwhite p-4">
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-3xl shadow-xl p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-zooerrorbg flex items-center justify-center mx-auto mb-4">
          <Icon name="Lock" className="text-xl text-zooerrortext" />
        </div>
        <h1 className="text-xl font-bold text-zooindigo">
          {t('domainWorkspace.mismatchTitle', { workspaceName })}
        </h1>
        <p className="text-sm text-zoomuted mt-2">
          {t('domainWorkspace.mismatchDescription', {
            workspaceName,
            brandName: environment.brandName,
          })}
        </p>
        {hasPlatformUrl ? (
          <Button asChild className="mt-6 w-full">
            <a href={buildPlatformUrl(environment.platformUrl, ROOT_PATH)}>
              {t('domainWorkspace.openPlatform', { brandName: environment.brandName })}
            </a>
          </Button>
        ) : null}
        <Button variant="outline" onClick={signOutHere} className="mt-3 w-full">
          {t('actions.logout')}
        </Button>
      </div>
    </div>
  );
}
